/**
 * Summary Dashboard API
 *
 * Endpoints:
 * - getSummaryList: Paginated list of POs with accordion status (filters, pipeline)
 * - getSummaryStats: Counts (total, inProgress, pending, completed) for filters
 * - getSummaryExport: CSV export (same filters + optional columns)
 */

import { baseApi } from "./baseApi";
import type {
  ListSummaryRequest,
  SummaryRowResponse,
  SummaryListResponse,
  SummaryStatsResponse,
} from "@OrianaTypes";

// Product Report types
export interface ProductReportRequest {
  productId?: number;
  oemId?: number;
  categoryId?: number;
  clientId?: number;
  salesPersonId?: number;
  purchaseOrderId?: string;
}

export interface ProductReportItemResponse {
  productId: number;
  productName: string;
  categoryName: string;
  oemName: string;
  totalQuantity: number;
  totalSpareQuantity: number;
  avgPricePerUnit: number;
  totalPrice: number;
  finalPrice: number;
}

export interface ProductReportResponse {
  data: ProductReportItemResponse[];
}

interface ApiResponse<T> {
  success: boolean;
  data?: T;
  pagination?: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export const summaryApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getSummaryList: builder.query<SummaryListResponse, ListSummaryRequest | void>({
      query: (params) => ({
        url: "/po/summary",
        params: params || {},
      }),
      transformResponse: (response: ApiResponse<SummaryRowResponse[]>) => ({
        data: response.data ?? [],
        pagination: response.pagination ?? {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        },
      }),
      providesTags: (result) =>
        result
          ? [
              ...result.data.map(({ poId }) => ({ type: "PO" as const, id: poId })),
              { type: "PO", id: "SUMMARY_LIST" },
            ]
          : [{ type: "PO", id: "SUMMARY_LIST" }],
    }),

    getSummaryStats: builder.query<
      SummaryStatsResponse,
      Omit<ListSummaryRequest, "page" | "limit"> | void
    >({
      query: (params) => ({
        url: "/po/summary/stats",
        params: params || {},
      }),
      transformResponse: (response: SummaryStatsResponse & { success?: boolean }) => ({
        total: response.total ?? 0,
        inProgress: response.inProgress ?? 0,
        pending: response.pending ?? 0,
        completed: response.completed ?? 0,
        totalPrice:
          response.totalPrice != null ? Number(response.totalPrice) : undefined,
        finalPrice:
          response.finalPrice != null ? Number(response.finalPrice) : undefined,
        totalQuantity:
          response.totalQuantity != null ? Number(response.totalQuantity) : undefined,
      }),
      providesTags: [{ type: "PO", id: "SUMMARY_STATS" }],
    }),

    getSummaryExport: builder.mutation<
      string,
      (Omit<ListSummaryRequest, "page"> & { columns?: string }) | void
    >({
      query: (params) => ({
        url: "/po/summary/export",
        params: params || {},
        responseHandler: "text",
      }),
    }),

    getReports: builder.query<ProductReportResponse, ProductReportRequest>({
      query: (params) => ({
        url: "/po/summary/report",
        params: params || {},
      }),
      transformResponse: (response: ApiResponse<ProductReportItemResponse[]>) => ({
        data: response.data ?? [],
      }),
      providesTags: [{ type: "PO", id: "PRODUCT_REPORT" }],
    }),
  }),

  overrideExisting: false,
});

export const {
  useGetSummaryListQuery,
  useGetSummaryStatsQuery,
  useGetSummaryExportMutation,
  useLazyGetSummaryListQuery,
  useLazyGetSummaryStatsQuery,
  useLazyGetReportsQuery,
} = summaryApi;
